import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const pad = (n) => String(n).padStart(2, "0");
const parts = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return [["Days", Math.floor(s / 86400)], ["Hrs", Math.floor((s % 86400) / 3600)], ["Min", Math.floor((s % 3600) / 60)], ["Sec", s % 60]];
};

export const DropCountdown = ({ settings }) => {
  const dropAt = settings?.drop_at ? new Date(settings.drop_at).getTime() : null;
  const [left, setLeft] = useState(dropAt ? dropAt - Date.now() : 0);
  const [code, setCode] = useState(settings?.drop_code ?? "");

  useEffect(() => {
    setCode(settings?.drop_code ?? "");
  }, [settings]);

  useEffect(() => {
    if (!dropAt) return undefined;
    const t = setInterval(() => setLeft(dropAt - Date.now()), 1000);
    return () => clearInterval(t);
  }, [dropAt]);

  const live = dropAt && left <= 0;
  useEffect(() => {
    if (!live || code) return undefined;
    const t = setTimeout(() => {
      api.get("/settings").then((r) => setCode(r.data?.drop_code ?? "")).catch(() => null);
    }, 1200);
    return () => clearTimeout(t);
  }, [live, code]);

  if (!dropAt || Date.now() - dropAt > 72 * 3600 * 1000) return null;

  return (
    <section className="relative overflow-hidden border-t border-white/10 bg-[#0b0b11]" data-testid="drop-countdown">
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(600px circle at 85% 50%, rgba(255,61,200,0.16), transparent 60%)" }} />
      <div className="relative mx-auto grid max-w-[1440px] gap-8 px-5 py-14 md:px-10 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-4">
          <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-[#ff3dc8]">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#ff3dc8] shadow-[0_0_12px_#ff3dc8]" /> {live ? "Drop is live" : "Next drop"}
          </p>
          <h2 className="mt-4 font-display text-5xl uppercase leading-none text-white md:text-6xl" data-testid="drop-title">{settings?.drop_title || "New accounts drop"}</h2>
        </div>
        {!live ? (
          <div className="flex gap-3 sm:gap-5 lg:col-span-5" data-testid="drop-timer">
            {parts(left).map(([label, v]) => (
              <div key={label} className="min-w-0 flex-1 border border-white/10 bg-black/40 px-2 py-4 text-center">
                <motion.p key={v} initial={{ y: -8, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35 }} className="font-display text-5xl leading-none text-white md:text-7xl">{pad(v)}</motion.p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-base text-slate-300 lg:col-span-5">Fresh accounts are in the lineup. Use the code before it runs out.</p>
        )}
        <div className="lg:col-span-3">
          {code ? (
            <motion.button
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={() => navigator.clipboard?.writeText(code).then(() => toast.success(`${code} copied`))}
              data-testid="drop-code"
              className="flex w-full items-center justify-between gap-3 border-2 border-dashed border-[#e9d5ff] bg-[#e9d5ff]/10 px-5 py-4 transition-shadow hover:shadow-[0_0_30px_rgba(233,213,255,0.35)]"
            >
              <span className="font-mono text-2xl font-extrabold tracking-widest text-[#e9d5ff]">{code}</span>
              <Copy className="h-5 w-5 text-[#e9d5ff]" />
            </motion.button>
          ) : (
            <div className="flex items-center gap-3 border border-dashed border-white/20 px-5 py-4" data-testid="drop-code-locked">
              <Lock className="h-5 w-5 text-slate-400" />
              <span className="font-mono text-xs uppercase tracking-widest text-slate-300">{live ? "No code this drop" : "Promo code reveals at zero"}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
